import {useEffect, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import CreateSelectEntity from "@/components/CreateSelectEntity";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";
import Rating from "@/components/Rating";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {ClubAPI} from "@/api/club";
import {PlayerAPI} from "@/api/player";
import {PlayerValuationAPI} from "@/api/player_valuation";
import {ScoutingReportAPI} from "@/api/scouting_report";

import {
	DEFAULT_CATEGORY_ID,
	ERROR_OPERATION,
	REQUIRED_FIELD,
	SUCCESSFUL_OPERATION,
	dateFormat,
	baseExternMatchParameters,
	baseExternTrainingParameters,
	externalValuationOptions,
	getInitialBaseValuationParameters,
	initialValuationParameters,
	matchTypesOptions,
	playerRoles,
	proExternMatchParameters,
	proExternTrainingParameters,
	valuationsTypes,
} from "@/utils/constants";
import {
	checkRequiredFields,
	getTextAreaField,
	getTextField,
} from "@/utils/functions";
import {useCategories, usePlayers} from "@/utils/hooks";

import "../Valuation/index.scss";
import {useTranslation} from "react-i18next";

const HandleValuationPage = (props) => {
	const {edit, external = false, handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const seasonId = useSelector((state) => state?.season?.id);
	const {t} = useTranslation();

	const type = new URLSearchParams(useLocation().search).get("type");
	const playerId = new URLSearchParams(useLocation().search).get("playerId");
	const [playerData, setPlayerData] = useState();

	const [loading, setLoading] = useState(false);

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
		formatted: true,
	});
	const {players} = usePlayers({
		setLoading,
		handleNotification: undefined,
		chargeData: external,
		goalkeepers: false,
		prospect: external,
	});

	const [errors, setErrors] = useState([]);
	const [fields, setFields] = useState({});
	const [saveOption, setSaveOption] = useState(edit ? "select" : "new");

	const [valuationParameters, setValuationParameters] = useState([]);

	const isScoutingValuation =
		fields?.valuationType?.value && fields?.valuationType?.value === "scout";
	const isQuickValuation =
		fields?.valuationType?.value && fields?.valuationType?.value === "quick";

	const [clubs, setClubs] = useState([]);
	const getClubs = async () => {
		try {
			const response = await ClubAPI.list();
			setClubs(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};
	useEffect(() => {
		if (external) {
			getClubs();
		}
	}, [external]);

	const createPlayer = async () => {
		try {
			const body = {
				firstName: fields?.firstName ? fields.firstName : "",
				lastName: fields?.lastName,
				dateOfBirth: fields?.dateOfBirth
					? moment(fields.dateOfBirth).format("YYYY-MM-DD")
					: undefined,
				favoriteFoot: fields?.favoriteFoot,
				goalkeeper: fields?.playerPrimaryRole?.value === "Portiere",
				primaryRole: fields?.playerPrimaryRole?.value,
				prospect: true,
				playerCategoryId: DEFAULT_CATEGORY_ID,
			};

			const response = await PlayerAPI.create(body);
			return response;
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};
	const createClub = async (name, fieldKey) => {
		try {
			const response = await ClubAPI.create({name, prospect: true});
			setFields({
				...fields,
				[fieldKey]: {value: response.id, label: response.name},
			});
			getClubs();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const getPlayerData = async () => {
		setLoading(true);
		try {
			const response = await PlayerAPI.getById(playerId, {seasonId});
			setPlayerData(response);
			if (edit) {
				getValuation();
			} else {
				setLoading(false);
			}
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const getValuation = async () => {
		setLoading(true);
		try {
			const response =
				type === "scout"
					? await ScoutingReportAPI.getById(id)
					: await PlayerValuationAPI.getById(id);

			if (external) {
				let valuationType = {};
				switch (response.valuationType) {
					case undefined:
						valuationType = valuationsTypes.filter(
							(t) => t.value === "scout",
						)?.[0];
						break;
					case 2:
					case 4:
						valuationType = valuationsTypes.filter(
							(t) => t.value === "match",
						)?.[0];
						break;
					case 3:
					case 5:
						valuationType = valuationsTypes.filter(
							(t) => t.value === "training",
						)?.[0];
						break;
					case 6:
						valuationType = valuationsTypes.filter(
							(t) => t.value === "quick",
						)?.[0];
						break;
					default:
						break;
				}
				response.valuationType = valuationType;

				if (valuationType.value === "scout") {
					response.playerClub = {
						value: response.playerClub?.id,
						label: response.playerClub?.name,
					};
					response.matchType = matchTypesOptions?.filter(
						(mt) => mt.ordinal === response.matchType,
					)?.[0];
				} else {
					response.prospectClub = {
						value: response.club?.id,
						label: response.club?.name,
					};
				}

				response.prospectCategory = {
					value: response.category?.id,
					label: response.category?.name,
				};

				response.prospectPlayer = {
					value: response.player?.id,
					label:
						(response.player?.firstName || "") +
						" " +
						response.player?.lastName,
				};

				const ratingLabel =
					valuationType.value === "scout" ? "valuation" : "finalOverallRating";
				response.rating =
					response[ratingLabel] !== null && response[ratingLabel] !== undefined
						? externalValuationOptions.filter(
								(v) => v.value === response[ratingLabel].toString(),
							)
						: undefined;

				setFields(response);
			} else {
				setFields({
					...response,
				});
			}
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (!external && playerId) {
			getPlayerData();
		}
	}, [external, edit, players, playerId]);

	useEffect(() => {
		if (edit && external) {
			if (categories?.length && clubs?.length && players?.length) {
				getValuation();
			}
		}
	}, [external, edit, categories, clubs, players]);

	const getRequiredFields = () => {
		const reqFields = [];
		if (isScoutingValuation) {
			reqFields.push("reportDate", "scout");
		} else {
			reqFields.push("valuationDate", "valuationAuthor", "valuationText");
		}

		if (external) {
			if (saveOption === "new") {
				reqFields.push(
					"valuationType",
					isScoutingValuation ? "playerClub" : "prospectClub",
					"prospectCategory",
					"lastName",
					"playerPrimaryRole",
				);
			} else {
				reqFields.push(
					"valuationType",
					isScoutingValuation ? "playerClub" : "prospectClub",
					"prospectCategory",
					"prospectPlayer",
				);
			}
		}
		return reqFields;
	};

	const saveValuation = async () => {
		const errors = checkRequiredFields(getRequiredFields(), fields);
		setErrors(errors);

		if (!errors.length) {
			setLoading(true);

			const body = Object.assign({}, fields);

			if (external) {
				if (saveOption === "new") {
					const playerResponse = await createPlayer();
					body.playerId = playerResponse?.id;
				} else {
					body.playerId = body.prospectPlayer?.value;
				}

				if (isScoutingValuation) {
					body.playerClubId = body.playerClub?.value;
					body.playerClub = {
						id: body.playerClub?.value,
						name: body.playerClub?.label,
					};
					// body.matchType = matchTypesOptions?.filter(
					// 	(mt) => mt.value === body.matchType?.value,
					// )?.[0]?.ordinal;
					// body.valuation = body.rating?.value;
				} else {
					body.clubId = body.prospectClub?.value;
					body.club = {
						id: body.prospectClub?.value,
						name: body.prospectClub?.label,
					};
					// body.finalOverallRating = body.rating?.value;
				}
				body.playerCategoryId = body.prospectCategory?.value;

				const isCategoryPro = !fields.prospectCategory?.basicActivities;
				body.valuationType =
					body.valuationType?.value === "match"
						? isCategoryPro
							? 2
							: 4
						: body.valuationType?.value === "quick"
							? 6
							: isCategoryPro
								? 3
								: 5;
			} else {
				body.playerId = playerId;
				body.valuationType = 1;
			}

			if (isScoutingValuation) {
				body.matchType = matchTypesOptions?.filter(
					(mt) => mt.value === body.matchType?.value,
				)?.[0]?.ordinal;
				body.valuation = body.rating?.value;
				body.reportDate = moment(body.reportDate).format("YYYY-MM-DD");
			} else {
				body.valuationDate = moment(body.valuationDate).format("YYYY-MM-DD");
				body.finalOverallRating = body.rating?.value;
			}

			try {
				let valuationData;

				if (isScoutingValuation) {
					valuationData = edit
						? await ScoutingReportAPI.update(id, body)
						: await ScoutingReportAPI.create(body);
				} else {
					valuationData = edit
						? await PlayerValuationAPI.update(id, body)
						: await PlayerValuationAPI.create(body);
				}

				setTimeout(() => {
					const currentId = id || valuationData?.id;
					navigate(
						external
							? isScoutingValuation
								? "/scouting/scout-valuations/" + currentId
								: "/scouting/valuations/" + currentId
							: isScoutingValuation
								? "/scouting/scout-valuations/" + currentId
								: "/players-valuations/" + currentId + "?playerId=" + playerId,
					);
					handleNotification(SUCCESSFUL_OPERATION, "success");
				}, 1000);
			} catch (error) {
				handleNotification(ERROR_OPERATION, "error");
				setLoading(false);
			}
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		saveValuation();
	};

	const goBack = () => {
		navigate(-1);
	};

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: goBack,
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled: checkRequiredFields(getRequiredFields(), fields).length
				? true
				: false,
		},
	];

	const breadcrumbsElements = [
		{
			label: "Valutazioni",
			path: external ? "/scouting/valuations" : "/players/" + playerId,
		},
	];

	const playerLabel =
		external && fields?.player
			? fields?.player?.firstName
				? fields?.player?.firstName + " " + fields?.player?.lastName
				: fields?.player?.lastName
			: playerData
				? playerData?.firstName + " " + playerData?.lastName
				: "---";

	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{
				label:
					moment(
						isScoutingValuation ? fields?.reportDate : fields?.valuationDate,
					).format(dateFormat) +
					" " +
					playerLabel,
				path: external
					? "/scouting/valuations/" + id
					: "/players-valuations/" + id + "?playerId=" + playerId,
			},
		);
	}

	const defaultTextField = {
		values: fields,
		setValues: setFields,
		errors,
		setErrors,
	};

	const getFinalAvg = () => {
		let totalRatings = 0,
			totalRatingsInserted = 0;

		valuationParameters.forEach((i) => {
			const currentValue = fields?.[i.fieldKeyRating];
			if (currentValue && currentValue > 0) {
				totalRatings += currentValue;
				totalRatingsInserted++;
			}
		});

		return !totalRatingsInserted
			? 0
			: +parseFloat(totalRatings / totalRatingsInserted).toFixed(2);
	};

	const getParametersList = () => {
		const playerCategory = categories?.filter(
			(c) => c.id === playerData?.playerCategoryId,
		)?.[0];

		const isBaseActivity =
			playerCategory?.basicActivities &&
			playerData?.playerCategoryName !== "Under 13";

		if (isBaseActivity) {
			return getInitialBaseValuationParameters(t, playerData);
		} else {
			return initialValuationParameters(t)?.filter(
				(p) =>
					!p.roles ||
					p.roles.indexOf(playerData?.primaryRole?.toLowerCase()) !== -1,
			);
		}
	};

	const getParameters = () => {
		if (external) {
			if (isScoutingValuation) {
				setValuationParameters([]);
			} else {
				setValuationParameters(
					!fields?.prospectCategory?.label ||
						!fields?.valuationType ||
						isQuickValuation
						? []
						: !fields.prospectCategory?.basicActivities
							? fields?.valuationType?.value === "match"
								? proExternMatchParameters(t)
								: proExternTrainingParameters(t)
							: fields?.valuationType?.value === "match"
								? baseExternMatchParameters(t)
								: baseExternTrainingParameters(t),
				);
			}
		} else {
			if (playerData) {
				if (isScoutingValuation) {
					setValuationParameters([]);
				} else {
					setValuationParameters(
						// !fields?.valuationType || isQuickValuation
						// 	? []
						// :
						getParametersList(),
					);
				}
			}
		}
	};

	useEffect(() => {
		getParameters();
	}, [playerData, categories, fields?.valuationType, fields?.prospectCategory]);

	const selectPlayers = players.map((p) => {
		return {
			value: p.id,
			label: p.firstName + " " + p.lastName,
		};
	});
	const currentPlayer = players?.filter(
		(p) => p.id === fields?.prospectPlayer?.value,
	)?.[0];

	const getDateField = (fieldKey) => {
		return (
			<VLFDatePicker
				label="Data"
				value={fields[fieldKey]}
				onChange={(value) => {
					setErrors([]);
					setFields({...fields, [fieldKey]: value});
				}}
				required
				format="DD-MM-YYYY"
				size="small"
				helperText={errors.indexOf(fieldKey) !== -1 ? REQUIRED_FIELD : ""}
				error={errors.indexOf(fieldKey) !== -1}
			/>
		);
	};
	const getClubSelect = (fieldKey) => {
		return (
			<VLFSelectNew
				options={clubs.map((c) => {
					return {
						value: c.id,
						label: c.name,
					};
				})}
				value={fields[fieldKey]}
				onChange={(newValue) => {
					setErrors([]);
					setFields({
						...fields,
						[fieldKey]: newValue,
					});
				}}
				onCreateOption={(name) => createClub(name, fieldKey)}
				label="Squadra"
				required
				helperText={errors.indexOf([fieldKey]) !== -1 ? REQUIRED_FIELD : ""}
				error={errors.indexOf([fieldKey]) !== -1}
			/>
		);
	};
	const getScoutingParameters = () => {
		const fullParameters = [
			{
				label: "Caratteristiche fisiche",
				fieldKey: "physicalCharacteristicsType",
				fieldKeyDescr: "physicalCharacteristicsDescription",
			},
			{
				label: "Caratteristiche psicologiche",
				fieldKey: "psychologicalCharacteristicsType",
				fieldKeyDescr: "psychologicalCharacteristicsDescription",
			},
		].map((el, k) => {
			return (
				<div key={k} className="col-sm-12 col-md-6">
					<div className="field-label-container">
						<div className="field-label">{el.label}</div>
					</div>
					{getTextField({
						...defaultTextField,
						fieldKey: el.fieldKey,
						placeholder: el.label,
					})}
					{getTextAreaField({
						...defaultTextField,
						fieldKey: el.fieldKeyDescr,
						placeholder: "...",
						minRows: 3,
						maxRows: 3,
					})}
				</div>
			);
		});

		return (
			<>
				{fullParameters}
				<div className="col-sm-12 col-md-6">
					{getTextAreaField({
						...defaultTextField,
						label: "Qualità atletiche",
						fieldKey: "athleticQualities",
						minRows: 3,
						maxRows: 3,
					})}
				</div>
				<div className="col-sm-12 col-md-6">
					{getTextAreaField({
						...defaultTextField,
						label: "Qualità coordinative",
						fieldKey: "coordinationQualities",
						minRows: 3,
						maxRows: 3,
					})}
				</div>
				<div className="col-sm-12 col-md-6">
					{getTextAreaField({
						...defaultTextField,
						label: "Qualità strutturali",
						fieldKey: "structuralQualities",
						minRows: 3,
						maxRows: 3,
					})}
				</div>
				<div className="col-sm-12 col-md-6">
					{getTextAreaField({
						...defaultTextField,
						label: "Qualità tecniche",
						fieldKey: "technicalQualities",
						minRows: 3,
						maxRows: 3,
					})}
				</div>
				<div className="col-sm-12 col-md-6">
					{getTextAreaField({
						...defaultTextField,
						label: "Qualità tattiche",
						fieldKey: "tacticalQualities",
						minRows: 3,
						maxRows: 3,
					})}
				</div>
			</>
		);
	};

	const valuationInfoColumnStyle = isScoutingValuation
		? "col-12 col-md-6 col-xl-3"
		: "col-12 col-md-6 col-xl-4";

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				{!external ? (
					<div className="row no-margin g-1">
						<div className="col-12 col-lg-6 padding-small">
							<div className="kpi-box-container">
								<div className="kpi-label">Giocatore</div>
								<div className="kpi-value">
									{playerData
										? playerData?.firstName + " " + playerData?.lastName
										: "---"}
								</div>
							</div>
						</div>
						<div className="col-12 col-lg-3 padding-small">
							<div className="kpi-box-container">
								<div className="kpi-label">Ruolo</div>
								<div className="kpi-value">
									{playerData?.primaryRole || "---"}
								</div>
							</div>
						</div>
						<div className="col-12 col-lg-3 padding-small">
							<div className="kpi-box-container">
								<div className="kpi-label">Categoria</div>
								<div className="kpi-value">
									{playerData?.playerCategoryName || "---"}
								</div>
							</div>
						</div>
					</div>
				) : null}
				{/* {external ? ( */}
				<div className="row no-margin mt-2">
					<div className="col-12 padding-small">
						<VLFCard
							title="Informazioni giocatore"
							content={
								<div className="row no-margin single-area-container">
									{external ? (
										<div className="col-12 padding-small">
											<CreateSelectEntity
												currentOption={saveOption}
												setCurrentOption={setSaveOption}
											/>
										</div>
									) : null}
									{external ? (
										saveOption === "new" ? (
											<>
												<div className="col-12 col-sm-6 col-xl-3">
													{getTextField({
														...defaultTextField,
														fieldKey: "firstName",
														label: t("name"),
													})}
												</div>
												<div className="col-12 col-sm-6 col-xl-3">
													{getTextField({
														...defaultTextField,
														fieldKey: "lastName",
														label: "Cognome",
														required: true,
													})}
												</div>
												<div className="col-12 col-sm-6 col-xl-3">
													<VLFDatePicker
														label="Data di nascita"
														value={fields.dateOfBirth}
														onChange={(value) => {
															setFields({...fields, dateOfBirth: value});
														}}
														format="DD-MM-YYYY"
													/>
												</div>
												<div className="col-12 col-sm-6 col-xl-3">
													<VLFSelectNew
														options={playerRoles}
														value={fields.playerPrimaryRole}
														onChange={(newValue) => {
															setFields({
																...fields,
																playerPrimaryRole: newValue,
															});
														}}
														sorted={false}
														label="Ruolo"
														required
													/>
												</div>
												<div className="col-12 col-sm-6 col-xl-3">
													{getTextField({
														...defaultTextField,
														fieldKey: "favoriteFoot",
														label: "Piede principale",
													})}
												</div>
											</>
										) : (
											<>
												<div className="col-12 col-sm-6">
													<VLFSelectNew
														options={selectPlayers}
														value={fields?.prospectPlayer}
														onChange={(value) => {
															setErrors([]);
															setFields({...fields, prospectPlayer: value});
														}}
														required
														helperText={
															errors.indexOf("prospectPlayer") !== -1
																? REQUIRED_FIELD
																: ""
														}
														error={errors.indexOf("prospectPlayer") !== -1}
														label={"Giocatore"}
													/>
												</div>
												{fields?.prospectPlayer && (
													<div className="col-12 col-sm-6 col-xl-3">
														<VLFDatePicker
															label="Data di nascita"
															value={
																currentPlayer && currentPlayer?.dateOfBirth
																	? moment(currentPlayer?.dateOfBirth).format(
																			dateFormat,
																		)
																	: "---"
															}
															editable={false}
														/>
													</div>
												)}
												{fields?.prospectPlayer && (
													<div className="col-12 col-sm-6 col-xl-3">
														<VLFTextField
															value={
																currentPlayer?.goalkeeper
																	? "Portiere"
																	: currentPlayer?.primaryRole
															}
															label="Ruolo"
															editable={false}
														/>
													</div>
												)}
											</>
										)
									) : null}
									{!isScoutingValuation ? (
										<div className="col-12 col-sm-6 col-xl-3">
											{getTextField({
												...defaultTextField,
												type: "number",
												fieldKey: "height",
												label: "Altezza (cm)",
											})}
										</div>
									) : null}
									{!isScoutingValuation ? (
										<div className="col-12 col-sm-6 col-xl-3">
											{getTextField({
												...defaultTextField,
												type: "number",
												fieldKey: "weight",
												label: "Peso (kg)",
											})}
										</div>
									) : null}
									{isScoutingValuation ? (
										<div className="col-12 col-sm-6 col-xl-3">
											{getTextField({
												...defaultTextField,
												fieldKey: "playerCity",
												label: "Città",
											})}
										</div>
									) : null}
									{isScoutingValuation ? (
										<div className="col-12 col-sm-6 col-xl-3">
											{getTextField({
												...defaultTextField,
												fieldKey: "playerDistrict",
												label: "Provincia",
											})}
										</div>
									) : null}
								</div>
							}
						/>
					</div>
				</div>
				{/* ) : null} */}
				<div className="row no-margin mt-2">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni valutazione"
							content={
								<>
									<div className="row no-margin">
										<div className="col-12 col-lg-10">
											<div className="row">
												{external ? (
													<div className={valuationInfoColumnStyle}>
														<VLFSelectNew
															options={valuationsTypes}
															value={fields.valuationType}
															onChange={(newValue) => {
																setErrors([]);
																setFields({
																	...fields,
																	valuationType: newValue,
																});
															}}
															label="Tipologia"
															required
															helperText={
																errors.indexOf("valuationType") !== -1
																	? REQUIRED_FIELD
																	: ""
															}
															error={errors.indexOf("valuationType") !== -1}
														/>
													</div>
												) : null}
												<div className={valuationInfoColumnStyle}>
													{getDateField(
														isScoutingValuation
															? "reportDate"
															: "valuationDate",
													)}
												</div>
												<div className={valuationInfoColumnStyle}>
													{getTextField({
														...defaultTextField,
														fieldKey: isScoutingValuation
															? "scout"
															: "valuationAuthor",
														label: "Relatore",
														required: true,
													})}
												</div>
												{/* {external ? (
													<> */}
												{external ? (
													<div className={valuationInfoColumnStyle}>
														{getClubSelect(
															isScoutingValuation
																? "playerClub"
																: "prospectClub",
														)}
													</div>
												) : null}
												{external ? (
													<div className={valuationInfoColumnStyle}>
														<VLFSelectNew
															options={categories.filter(
																(c) => c.label !== "Prima Squadra",
															)}
															sorted={false}
															value={fields.prospectCategory}
															onChange={(newValue) => {
																setErrors([]);
																setFields({
																	...fields,
																	prospectCategory: newValue,
																});
															}}
															label="Categoria"
															required
															helperText={
																errors.indexOf("prospectCategory") !== -1
																	? REQUIRED_FIELD
																	: ""
															}
															error={errors.indexOf("prospectCategory") !== -1}
														/>
													</div>
												) : null}
												{isScoutingValuation ? (
													<div className="col-12 col-sm-6 col-xl-3">
														{getTextField({
															...defaultTextField,
															fieldKey: "opposingTeam",
															label: "Squadra avversaria",
														})}
													</div>
												) : null}
												{isScoutingValuation ? (
													<div className="col-12 col-sm-6 col-xl-3">
														<VLFSelectNew
															options={matchTypesOptions}
															value={fields.matchType}
															onChange={(newValue) => {
																setFields({
																	...fields,
																	matchType: newValue,
																});
															}}
															label="Tipo di partita"
														/>
													</div>
												) : null}
												{isScoutingValuation ? (
													<div className="col-12 col-sm-6 col-xl-3">
														{getTextField({
															...defaultTextField,
															fieldKey: "observedPerformanceModel",
															label: "Modello prestativo",
														})}
													</div>
												) : null}
												{/* </>
												) : null} */}
												{!external ? (
													<>
														{!isQuickValuation && !isScoutingValuation ? (
															<div className={valuationInfoColumnStyle}>
																{getTextField({
																	...defaultTextField,
																	fieldKey: "timeInsideClub",
																	label: "Nel club dal",
																})}
															</div>
														) : null}
													</>
												) : null}
											</div>
										</div>
										<div className="col-12 col-lg-2">
											<div className="d-flex justify-content-center h-100 flex-column bordered-left">
												<div className="field-label-container justify-content-center">
													<div className="field-label">Voto finale</div>
												</div>
												<div className="stat-circle-container">
													<div className="stat-container">
														<div className="value">
															{!isQuickValuation && !isScoutingValuation
																? getFinalAvg()
																: fields.valuationRating || 0}
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</>
							}
						/>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Dettagli"
							content={
								<>
									<div className="row no-margin">
										{valuationParameters.map((el, k) => {
											return (
												<div key={k} className="col-sm-12 col-md-6">
													<div className="field-label-container">
														<div className="field-label">{el.label}</div>
													</div>
													<Rating
														selectedRating={fields[el.fieldKeyRating]}
														setSelectedRating={(value) => {
															setFields({
																...fields,
																[el.fieldKeyRating]: value,
															});
														}}
													/>
													{getTextAreaField({
														...defaultTextField,
														fieldKey: el.fieldKey,
														minRows: 3,
														maxRows: 3,
													})}
												</div>
											);
										})}
										{isScoutingValuation ? (
											<div className="col-12 col-md-6">
												{getTextField({
													...defaultTextField,
													fieldKey: "playerPrevalentAttitude",
													label: "Attitudine prevalente",
												})}
											</div>
										) : null}
										{isScoutingValuation ? (
											<div className="col-12 col-md-6">
												{getTextField({
													...defaultTextField,
													fieldKey: "playerRole",
													label: "Descrizione ruolo",
												})}
											</div>
										) : null}
										{
											/*external && */ isScoutingValuation
												? getScoutingParameters()
												: null
										}
										{!external && !isScoutingValuation && !isQuickValuation ? (
											<div className="col-sm-12 col-md-6">
												{getTextAreaField({
													...defaultTextField,
													fieldKey: "strengthAreas",
													label: "Punti di forza, aree migliorabili",
												})}
											</div>
										) : null}
										{
											/*!external ||*/ !isQuickValuation ? (
												<div className="col-sm-12 col-md-6">
													{getTextAreaField({
														...defaultTextField,
														fieldKey: "finalOverallValuation",
														label: isScoutingValuation
															? "Giudizio generale"
															: "Valutazione complessiva finale",
													})}
												</div>
											) : null
										}
										{
											/*external && */ isScoutingValuation ? (
												<div className="col-sm-12 col-md-6">
													{getTextAreaField({
														...defaultTextField,
														label: "Margini di miglioramento",
														fieldKey: "marginsImprovement",
														minRows: 3,
														maxRows: 3,
													})}
												</div>
											) : null
										}
										{
											/*external && */ isScoutingValuation ? (
												<div className="col-sm-12 col-md-6">
													{getTextAreaField({
														...defaultTextField,
														label: "Eventuali comunicazioni interne",
														fieldKey: "internalCommunications",
														minRows: 3,
														maxRows: 3,
													})}
												</div>
											) : null
										}
										{(isQuickValuation || isScoutingValuation) && (
											<div className="col-sm-12 col-md-6">
												<VLFSelectNew
													options={externalValuationOptions}
													value={fields.rating}
													sorted={false}
													onChange={(newValue) => {
														setFields({
															...fields,
															rating: newValue,
														});
													}}
													label="Esito"
												/>
											</div>
										)}
										{
											/*external && */ isQuickValuation ||
											isScoutingValuation ? (
												<div className="col-sm-12 col-md-6">
													<div className="field-label-container">
														<div className="field-label">Punteggio</div>
													</div>
													<Rating
														selectedRating={fields.valuationRating}
														setSelectedRating={(value) => {
															setFields({
																...fields,
																valuationRating: value,
															});
														}}
													/>
												</div>
											) : null
										}
									</div>
									{!isScoutingValuation ? (
										<div className="row no-margin">
											<div className="col-12">
												{getTextAreaField({
													...defaultTextField,
													fieldKey: "valuationText",
													label:
														external || isQuickValuation
															? "Relazione generale"
															: "Valutazione iniziale",
													required: true,
													minRows: 10,
													maxRows: 10,
												})}
											</div>
										</div>
									) : null}
								</>
							}
						/>
					</div>
				</div>
			</div>
		</Page>
	);
};

export default HandleValuationPage;
